import { Input } from '@onwardtravelsolutions/package-react/Components';
import { ValidationStatus } from '@onwardtravelsolutions/package-react/Enums';
import { useFieldFocus } from '@onwardtravelsolutions/package-react/Hooks';
import { Notice } from '@onwardtravelsolutions/package-react/Types';
import { generateUuid } from '@onwardtravelsolutions/package-react/Utilities/strings';
import { getValidation } from '../../Utilities/validation';
import Field from './Field';
import FieldValidation from './FieldValidation';

type InputProps = {
    id: string;
    label?: string;
    type: 'text' | 'email' | 'tel' | 'hidden';
    className?: string;
    status?: ValidationStatus;
    notices?: Notice[];
    ref?: React.RefObject<null | React.ReactNode>;
    isDirty?: boolean;
    value?: string;
    setValue?: (value: any) => void;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
} & React.InputHTMLAttributes<HTMLInputElement>;

export default function InputField({
    id,
    type,
    label = null,
    className = null,
    value = '',
    onChange = null,
    setValue = null,
    status,
    notices = [],
    autoComplete,
    name,
    isDirty = false,
    ...rest
}: InputProps) {
    const validation = getValidation(status);
    const { isFocused, focusHandlers } = useFieldFocus(false);
    const isHidden = type === 'hidden';

    const isAutocomplete = autoComplete !== 'off';
    const displayName = isAutocomplete ? name : `${generateUuid()}-${name}`;

    const input = (
        <Input
            {...{
                id: `field-${id}`,
                autoComplete: 'off',
                type,
                className: isHidden
                    ? ''
                    : `inset-shadow-sm bg-gray-50 dark:bg-neutral-700 border ${validation.borderClasses} rounded w-full dark:text-neutral-200 border:solid focus:outline-none border-2`,
                onChange:
                    onChange ??
                    ((event: React.ChangeEvent<HTMLInputElement>) =>
                        setValue?.(event.currentTarget.value)),
                name: displayName,
                value,
                ...focusHandlers,
                ...rest,
            }}
        />
    );

    return (
        <Field id={id} label={label} className={className}>
            {!isAutocomplete ? (
                <input
                    type={type}
                    style={{ display: 'none' }}
                    name={name}
                    autoComplete={'default'}
                />
            ) : null}
            {isHidden ? (
                input
            ) : (
                <FieldValidation
                    id={id}
                    name={name}
                    status={status}
                    notices={notices}
                    showNotices={isFocused}
                >
                    {input}
                </FieldValidation>
            )}
        </Field>
    );
}
